import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle2, Activity, Server, AlertCircle, Clock, TrendingUp, RefreshCw, Zap, MonitorCheck, Calendar } from 'lucide-react';
import { formatDate, SEVERITY_COLORS } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RefreshIndicator({ lastUpdate, interval }) {
  const formatInterval = (secs) => {
    if (secs >= 60) return `${Math.floor(secs / 60)} min`;
    return `${secs}s`;
  };
  
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <RefreshCw className="h-3 w-3" />
      <span>Auto: {formatInterval(interval)}</span>
      {lastUpdate && (
        <span className="text-muted-foreground/60">• Último: {new Date(lastUpdate).toLocaleTimeString('pt-BR')}</span>
      )}
    </div>
  );
}

function StatsCard(props) {
  const { title, value, subtitle, icon: Icon, variant, testId, trend, trendLabel } = props;
  
  const getGradientClass = () => {
    const styles = {
      success: 'from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
      error: 'from-red-500/10 to-red-500/5 border-red-500/20',
      warning: 'from-amber-500/10 to-amber-500/5 border-amber-500/20',
      info: 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
      neutral: 'from-slate-500/10 to-slate-500/5 border-slate-500/20'
    };
    return styles[variant] || styles.info;
  };
  
  const getIconBgClass = () => {
    const colors = {
      success: 'bg-emerald-500/20 text-emerald-600',
      error: 'bg-red-500/20 text-red-600',
      warning: 'bg-amber-500/20 text-amber-600',
      info: 'bg-blue-500/20 text-blue-600',
      neutral: 'bg-slate-500/20 text-slate-600'
    };
    return colors[variant] || colors.info;
  };

  const getValueClass = () => {
    const colors = {
      success: 'text-emerald-600',
      error: 'text-red-600',
      warning: 'text-amber-600',
      info: 'text-blue-600',
      neutral: 'text-slate-600'
    };
    return colors[variant] || colors.info;
  };

  return (
    <Card 
      className={`relative overflow-hidden bg-gradient-to-br ${getGradientClass()} border backdrop-blur-sm hover:shadow-lg transition-all duration-300`}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <div className="flex items-baseline gap-2">
              <p className={`text-4xl font-bold tracking-tight ${getValueClass()}`}>
                {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
              </p>
              {subtitle && (
                <span className="text-sm text-muted-foreground">{subtitle}</span>
              )}
            </div>
            {trend !== undefined && (
              <div className="flex items-center gap-1 text-xs">
                <TrendingUp className={`h-3 w-3 ${trend >= 0 ? 'text-emerald-500' : 'text-red-500 rotate-180'}`} />
                <span className={trend >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                  {trend >= 0 ? '+' : ''}{trend}%
                </span>
                {trendLabel && <span className="text-muted-foreground">{trendLabel}</span>}
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${getIconBgClass()}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryChart(props) {
  const { data } = props;
  
  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-muted-foreground">
        Nenhum evento por categoria
      </div>
    );
  }
  
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const width = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        return (
          <div key={index} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>{item.name}</span>
              <span className="font-medium">{item.value}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TopHostsList(props) {
  const { hosts } = props;
  
  if (!hosts || hosts.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Nenhum host com alarmes
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {hosts.map((host, index) => (
        <div key={host.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/50" data-testid={`top-host-${index}`}>
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono text-muted-foreground w-6">#{index + 1}</span>
            <span className="text-sm font-medium truncate max-w-[200px]">{host.name}</span>
          </div>
          <Badge variant="secondary">{host.count} eventos</Badge>
        </div>
      ))}
    </div>
  );
}

function RecentEventsList(props) {
  const { events } = props;
  
  if (!events || events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        Nenhum evento recente
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {events.map((event, index) => (
        <div key={event.id} className="p-3 rounded-lg border bg-card" data-testid={`recent-event-${index}`}>
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-sm font-medium line-clamp-1">{event.name}</span>
            <Badge className="shrink-0" style={{ backgroundColor: SEVERITY_COLORS[event.severity] }}>
              {event.severity_label}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-mono">{event.host}</span>
            <span>{formatDate(event.timestamp)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        <Card><CardContent className="p-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        <Card><CardContent className="p-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
        <Card><CardContent className="p-6"><Skeleton className="h-16 w-full" /></CardContent></Card>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [topHostsPeriod, setTopHostsPeriod] = useState('24');
  const [topHostsData, setTopHostsData] = useState(null);
  const [topHostsLoading, setTopHostsLoading] = useState(false);
  const [trendData, setTrendData] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [categoryPeriod, setCategoryPeriod] = useState('24');
  const [categoryData, setCategoryData] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [mttrPeriods, setMttrPeriods] = useState(null);
  const { getAuthHeader } = useAuthStore();
  const { refreshInterval } = useThemeStore();

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/realtime`, { headers: getAuthHeader() });
      setData(response.data);
      setLastUpdate(new Date().toISOString());
      setError(null);
      
      // Fetch trend data
      try {
        const trendResponse = await axios.get(`${API}/dashboard/trends?days=30`, { headers: getAuthHeader() });
        setTrendData(trendResponse.data);
      } catch (trendErr) {
        console.error('Error fetching trends:', trendErr);
      }
      
      // Fetch MTTR periods
      try {
        const mttrResponse = await axios.get(`${API}/mttr-periods`, { headers: getAuthHeader() });
        setMttrPeriods(mttrResponse.data?.periods || []);
      } catch (mttrErr) {
        console.error('Error fetching MTTR periods:', mttrErr);
      }
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.detail || 'Failed to fetch dashboard data';
      
      // Check if it's a token expired error
      if (status === 401 && message.toLowerCase().includes('token')) {
        setError('Sessão expirada. Faça login novamente.');
      } else if (status === 401) {
        setError('Sessão expirada. Faça login novamente.');
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchTopHosts = useCallback(async () => {
    setTopHostsLoading(true);
    try {
      const hours = topHostsPeriod === '24' ? 24 : parseInt(topHostsPeriod) * 24;
      const response = await axios.get(`${API}/top-hosts?hours=${hours}&limit=10`, { headers: getAuthHeader() });
      setTopHostsData(response.data);
    } catch (err) {
      console.error('Error fetching top hosts:', err);
    } finally {
      setTopHostsLoading(false);
    }
  }, [getAuthHeader, topHostsPeriod]);

  const fetchCategoryEvents = useCallback(async () => {
    setCategoryLoading(true);
    try {
      const hours = categoryPeriod === '24' ? 24 : parseInt(categoryPeriod) * 24;
      const response = await axios.get(`${API}/category-events?hours=${hours}`, { headers: getAuthHeader() });
      setCategoryData(response.data);
    } catch (err) {
      console.error('Error fetching category events:', err);
    } finally {
      setCategoryLoading(false);
    }
  }, [getAuthHeader, categoryPeriod]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  useEffect(() => {
    fetchTopHosts();
  }, [fetchTopHosts]);

  useEffect(() => {
    fetchCategoryEvents();
  }, [fetchCategoryEvents]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  const hasZabbixConfig = data?.connected;
  const activeIncidents = data?.incidents?.active || data?.problems?.active || 0;
  const resolvedIncidents = data?.incidents?.resolved || data?.problems?.resolved || 0;
  const totalHosts = data?.hosts_summary?.total || 0;
  const hostsEnabled = data?.hosts_summary?.enabled || 0;
  const hostsUp = data?.hosts_summary?.up || 0;
  const hostsWarning = data?.hosts_summary?.warning || 0;
  const hostsDown = data?.hosts_summary?.down || 0;
  const hostsDisabled = data?.hosts_summary?.disabled || 0;
  const triggersEnabled = data?.triggers_summary?.enabled || data?.triggers_total || 0;
  const triggersDisabled = data?.triggers_summary?.disabled || 0;
  const triggersTotal = data?.triggers_summary?.total || data?.triggers_total || 0;
  const categoriesCount = data?.category_distribution?.length || 0;

  const getPeriodLabel = () => {
    switch(topHostsPeriod) {
      case '24': return '24 horas';
      case '7': return '7 dias';
      case '30': return '30 dias';
      case '60': return '60 dias';
      case '90': return '90 dias';
      default: return topHostsPeriod;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="realtime-dashboard">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!hasZabbixConfig && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Configure sua API Zabbix em Configurações → Zabbix API para visualizar os dados.
          </AlertDescription>
        </Alert>
      )}

      {/* Refresh Indicator */}
      <div className="flex justify-end">
        <RefreshIndicator lastUpdate={lastUpdate} interval={refreshInterval} />
      </div>

      {/* Row 1: Main Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          title="Incidentes Ativos" 
          value={activeIncidents} 
          icon={AlertTriangle} 
          variant="error" 
          testId="active-incidents-card" 
        />
        <StatsCard 
          title="Resolvidos (24h)" 
          value={resolvedIncidents} 
          icon={CheckCircle2} 
          variant="success" 
          testId="resolved-incidents-card" 
        />
        <StatsCard 
          title="Total de Hosts" 
          value={totalHosts} 
          icon={Server} 
          variant="info" 
          testId="total-hosts-card" 
        />
        <Card 
          className="relative overflow-hidden bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 backdrop-blur-sm hover:shadow-lg transition-all duration-300"
          data-testid="triggers-card"
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Triggers</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold tracking-tight text-emerald-600">{triggersEnabled.toLocaleString('pt-BR')}</span>
                  <span className="text-xl text-muted-foreground">/</span>
                  <span className="text-2xl font-semibold text-slate-400">{triggersDisabled.toLocaleString('pt-BR')}</span>
                </div>
                <p className="text-xs text-muted-foreground">Ativas / Desativadas</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-500/20 text-amber-600">
                <Zap className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: MTTR Card - full width, horizontal layout */}
      <Card className="relative overflow-hidden border border-blue-500/20 bg-blue-50/30" data-testid="mttr-card">
        <CardContent className="py-3 px-5 flex items-center justify-between">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tempo Médio de Resolução</p>
          <div className="flex items-center gap-8">
            {[
              { label: '24h' },
              { label: '7d' },
              { label: '30d' },
            ].map((period) => {
              const found = mttrPeriods?.find(p => p.label === period.label);
              const val = found ? found.mttr_minutes : 0;
              return (
                <div key={period.label} className="flex items-baseline gap-1">
                  <span className="text-lg font-bold font-mono tracking-tight text-blue-600">{val}</span>
                  <span className="text-xs text-muted-foreground">min</span>
                  <span className="text-xs text-muted-foreground/60 ml-1">({period.label})</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Row 3: Severity Cards - single row, 5 equal cards */}
      <div className="grid grid-cols-5 gap-3" data-testid="severity-cards-row">
        {[
          { name: 'Disaster', cardClass: 'border-red-200 bg-red-50/50', valueClass: 'text-red-600' },
          { name: 'High', cardClass: 'border-orange-200 bg-orange-50/50', valueClass: 'text-orange-500' },
          { name: 'Average', cardClass: 'border-amber-200 bg-amber-50/50', valueClass: 'text-amber-500' },
          { name: 'Warning', cardClass: 'border-yellow-200 bg-yellow-50/40', valueClass: 'text-yellow-500' },
          { name: 'Information', cardClass: 'border-cyan-200 bg-cyan-50/40', valueClass: 'text-cyan-500' },
        ].map((sev) => (
          <Card 
            key={sev.name} 
            className={`relative overflow-hidden border hover:shadow-sm transition-all ${sev.cardClass}`}
            data-testid={`severity-card-${sev.name.toLowerCase()}`}
          >
            <CardContent className="p-3 flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{sev.name}</p>
              <p className={`text-xl font-bold font-mono tracking-tight ${sev.valueClass}`}>
                {data?.severity_distribution?.find(s => s.name === sev.name)?.value || 0}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Row 3: Incidents Line Chart (30 days) */}
      <Card data-testid="incidents-trend-chart">
        <CardHeader className="pb-0">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Incidentes (30 dias)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-2 pb-3 pt-2">
          {(() => {
            const trendItems = trendData?.incidents_trend || [];
            if (trendItems.length === 0) {
              return (
                <div className="flex items-center justify-center h-44 text-sm text-muted-foreground">
                  Sem dados de tendência disponíveis
                </div>
              );
            }

            const values = trendItems.map(d => d.value);
            const max = Math.max(...values, 1);
            const steps = 4;
            const stepValue = Math.ceil(max / steps);
            const yLabels = Array.from({ length: steps + 1 }, (_, i) => i * stepValue);
            const yMax = yLabels[yLabels.length - 1] || 1;

            const vw = 1000;
            const vh = 170;
            const padL = 48;
            const padR = 12;
            const padT = 12;
            const padB = 24;
            const plotW = vw - padL - padR;
            const plotH = vh - padT - padB;

            const n = trendItems.length;
            const pts = trendItems.map((d, i) => ({
              x: padL + (n > 1 ? (i / (n - 1)) * plotW : plotW / 2),
              y: padT + plotH - (d.value / yMax) * plotH,
              value: d.value,
              label: d.label,
            }));

            const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
            const areaPath = `${linePath} L${pts[n - 1].x},${padT + plotH} L${pts[0].x},${padT + plotH} Z`;

            const labelInterval = n <= 10 ? 1 : Math.ceil(n / 7);

            return (
              <svg viewBox={`0 0 ${vw} ${vh}`} width="100%" style={{ display: 'block', height: 'auto' }} onMouseLeave={() => setHoveredPoint(null)}>
                {/* Grid lines + Y labels */}
                {yLabels.map((val, i) => {
                  const y = padT + plotH - (val / yMax) * plotH;
                  return (
                    <g key={`y-${i}`}>
                      <line x1={padL} y1={y} x2={vw - padR} y2={y} stroke="#E5E7EB" strokeWidth={1} />
                      <text x={padL - 8} y={y + 4} textAnchor="end" fill="#9CA3AF" fontSize={11} fontFamily="Inter, sans-serif">{val}</text>
                    </g>
                  );
                })}

                {/* Axes */}
                <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#D1D5DB" strokeWidth={1} />
                <line x1={padL} y1={padT + plotH} x2={vw - padR} y2={padT + plotH} stroke="#D1D5DB" strokeWidth={1} />

                {/* Area + Line */}
                <path d={areaPath} fill="#EF4444" fillOpacity={0.06} />
                <path d={linePath} fill="none" stroke="#EF4444" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />

                {/* Data points + hover areas */}
                {pts.map((p, i) => {
                  const showXLabel = i === 0 || i === n - 1 || i % labelInterval === 0;
                  const isHovered = hoveredPoint === i;
                  return (
                    <g key={`p-${i}`}>
                      {/* Invisible wider hit area for easier hover */}
                      <circle cx={p.x} cy={p.y} r={16} fill="transparent" style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredPoint(i)}
                      />
                      {/* Vertical guide line on hover */}
                      {isHovered && (
                        <line x1={p.x} y1={padT} x2={p.x} y2={padT + plotH} stroke="#EF4444" strokeWidth={1} strokeDasharray="4,4" opacity={0.4} />
                      )}
                      {/* Dot */}
                      <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 4} fill={isHovered ? '#DC2626' : '#EF4444'} stroke="#fff" strokeWidth={2} style={{ transition: 'r 0.15s' }} />
                      {/* X-axis label */}
                      {showXLabel && (
                        <text x={p.x} y={padT + plotH + 16} textAnchor="middle" fill="#9CA3AF" fontSize={10} fontFamily="Inter, sans-serif">{p.label}</text>
                      )}
                      {/* Tooltip on hover */}
                      {isHovered && (
                        <g>
                          <rect x={p.x - 58} y={p.y - 36} width={116} height={24} rx={5} fill="#1F2937" opacity={0.93} />
                          <text x={p.x} y={p.y - 20} textAnchor="middle" fill="#fff" fontSize={10} fontWeight="500" fontFamily="Inter, sans-serif">
                            {p.label} — {p.value} incidentes
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>
            );
          })()}
        </CardContent>
      </Card>

      {/* Row 3: Hosts Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard 
          title="Hosts UP" 
          value={hostsUp} 
          icon={MonitorCheck} 
          variant="success" 
          testId="hosts-up-card" 
        />
        <StatsCard 
          title="Hosts Warning" 
          value={hostsWarning} 
          icon={AlertTriangle} 
          variant="warning" 
          testId="hosts-warning-card" 
        />
        <StatsCard 
          title="Hosts Down" 
          value={hostsDown} 
          icon={AlertCircle} 
          variant="error" 
          testId="hosts-down-card" 
        />
        <StatsCard 
          title="Desabilitados" 
          value={hostsDisabled} 
          icon={Server} 
          variant="neutral" 
          testId="hosts-disabled-card" 
        />
      </div>

      <Card data-testid="category-distribution-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Server className="h-4 w-4" />
              Eventos por Categoria
            </CardTitle>
            <Select value={categoryPeriod} onValueChange={setCategoryPeriod}>
              <SelectTrigger className="w-[130px] h-8 text-xs">
                <Calendar className="h-3 w-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24">24 horas</SelectItem>
                <SelectItem value="7">7 dias</SelectItem>
                <SelectItem value="30">30 dias</SelectItem>
                <SelectItem value="60">60 dias</SelectItem>
                <SelectItem value="90">90 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {categoryData?.total_events > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {categoryData.total_events.toLocaleString()} eventos no período
            </p>
          )}
        </CardHeader>
        <CardContent>
          {categoryLoading ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-6 w-full" />)}
            </div>
          ) : (
            <CategoryChart data={categoryData?.categories || data?.category_distribution} />
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Top Hosts Mais Alarmados
              </CardTitle>
              <Select value={topHostsPeriod} onValueChange={setTopHostsPeriod}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <Calendar className="h-3 w-3 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 horas</SelectItem>
                  <SelectItem value="7">7 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {topHostsData?.total_events > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {topHostsData.total_events.toLocaleString()} eventos no período
              </p>
            )}
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {topHostsLoading ? (
                <div className="space-y-2">
                  {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : (
                <TopHostsList hosts={topHostsData?.hosts || data?.top_hosts} />
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Eventos Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <RecentEventsList events={data?.recent_events} />
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
